import { useRef } from "react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Upload, Download, Fingerprint, FileSpreadsheet } from "lucide-react";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const years = [currentYear - 1, currentYear, currentYear + 1];

// Import Biometric Dialog
export const ImportDialog = ({
  open,
  onOpenChange,
  onImport,
  onDownloadTemplate,
  importing,
}) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      toast.error("Please select a CSV or Excel file");
      return;
    }

    await onImport(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Import Biometric Data
          </DialogTitle>
          <DialogDescription>Upload biometric attendance data from your device</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onDownloadTemplate} data-testid="download-biometric-template">
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="biometric-file-input"
            />
            <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-600 mb-2">Upload CSV or Excel file</p>
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()} 
              disabled={importing}
              data-testid="select-biometric-file"
            >
              {importing ? "Importing..." : "Select File"}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            File should contain: employee_code/biometric_id, date, check_in, check_out
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Export Dialog
export const ExportDialog = ({
  open,
  onOpenChange,
  month,
  setMonth,
  year,
  setYear,
  format,
  setFormat,
  onExport,
  onExportWithSalary,
  exporting,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Export Attendance</DialogTitle>
          <DialogDescription>Download attendance data for the selected period</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="input-field" data-testid="export-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Year</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="input-field" data-testid="export-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="input-field" data-testid="export-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button className="flex-1 btn-primary" onClick={onExport} disabled={exporting} data-testid="export-attendance-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              {exporting ? "Exporting..." : "Export Attendance"}
            </Button>
          </div>
          <Button 
            variant="outline" 
            className="w-full" 
            onClick={onExportWithSalary} 
            disabled={exporting}
            data-testid="export-with-salary-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export with Salary (Custom Format)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Quick Check-in Dialog
export const QuickCheckDialog = ({
  open,
  onOpenChange,
  employees,
  selectedEmployee,
  setSelectedEmployee,
  onCheckIn,
  onCheckOut,
  checkingIn,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Quick Attendance</DialogTitle>
          <DialogDescription>Mark check-in or check-out for an employee</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Select Employee</Label>
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="input-field" data-testid="quick-employee-select">
                <SelectValue placeholder="Select employee..." />
              </SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} {emp.employee_code ? `(${emp.employee_code})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button 
              className="flex-1 bg-green-600 hover:bg-green-700 text-white" 
              onClick={onCheckIn} 
              disabled={!selectedEmployee || checkingIn}
              data-testid="quick-checkin-btn"
            >
              Check In
            </Button>
            <Button 
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" 
              onClick={onCheckOut} 
              disabled={!selectedEmployee || checkingIn}
              data-testid="quick-checkout-btn"
            >
              Check Out
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
